// Boost Blenz Admin — Users & roles (super-admin only; requires manage_users).
// The ONE place people are managed. Add a person (name, email, checkmarks, tool levels), save,
// and the Worker opens the Cloudflare Access door for them and emails the sign-in steps. One
// sign-in then covers the dashboard, the ERP and the Help Desk CRM; each of those asks the
// admin what this person may do. A role sets default checkmarks; any single one can be
// overridden per person. Every change is enforced server-side and written to the audit log.

import React from "react";
import { FIGTREE, OSWALD, useT } from "../theme";
import { Icon, Title, Card, Badge, Btn, SectionTitle, Toggle, Eyebrow } from "../atoms";
import { Avatar } from "./appviews";
import { ConfirmDialog } from "./finance";
import {
  PERMISSIONS, ROLE_LABELS, ROLE_DEFAULTS, APP_KEYS, ERP_LEVELS, CRM_LEVELS, isRoleDefault, type Role,
} from "../permissions";

const ROLES: Role[] = ["owner", "ops", "support"];
const isApp = (k: string) => (APP_KEYS as readonly string[]).includes(k);
// Dashboard screens only — the separate tools get their own "Tools" block with a level.
const SCREENS = PERMISSIONS.filter((p) => !isApp(p.key));
const GROUPS = [...new Set(SCREENS.map((p) => p.group))];

type Levels = { erp: string; crm: string }; // "" = no access, else the level key ("owner" for owners)

export function UsersView({ me }: any) {
  const t = useT();
  const [users, setUsers] = React.useState<any[] | null>(null);
  const [accessConnected, setAccessConnected] = React.useState<boolean | null>(null);
  const [events, setEvents] = React.useState<any[]>([]);
  const [err, setErr] = React.useState<string | null>(null);
  const [note, setNote] = React.useState<{ tone: string; text: string } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [open, setOpen] = React.useState<string | null>(null); // expanded user email
  const [adding, setAdding] = React.useState(false);
  const [removing, setRemoving] = React.useState<any | null>(null);

  async function load() {
    try {
      const u = await fetch("/api/users").then((r) => (r.ok ? r.json() : Promise.reject(r.status)));
      const a = await fetch("/api/audit").then((r) => (r.ok ? r.json() : { events: [] })).catch(() => ({ events: [] }));
      setUsers(u.users || []);
      setAccessConnected(!!u.access?.connected);
      setEvents(a.events || []);
      setErr(null);
    } catch (e) {
      setErr(String(e));
      setUsers([]);
    }
  }
  React.useEffect(() => { load(); }, []);

  async function api(url: string, method: string, body?: any): Promise<any | null> {
    setBusy(true);
    try {
      const r = await fetch(url, {
        method,
        headers: { "content-type": "application/json" },
        body: body ? JSON.stringify(body) : undefined,
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || String(r.status));
      await load();
      setErr(null);
      return j;
    } catch (e: any) {
      setErr(String(e.message || e));
      return null;
    } finally {
      setBusy(false);
    }
  }

  const enc = (s: string) => encodeURIComponent(s);
  const patchUser = (email: string, body: any) => api(`/api/users/${enc(email)}`, "PATCH", body);
  const setPerms = (email: string, changes: Record<string, boolean>) => api(`/api/users/${enc(email)}/permissions`, "PUT", { changes });
  async function setLevel(u: any, app: "erp" | "crm", level: string) {
    const key = app === "erp" ? "inventory" : "crm";
    const on = level !== "";
    if (on !== (u.permissions || []).includes(key)) await setPerms(u.email, { [key]: on });
    if (on && level !== "owner") await patchUser(u.email, app === "erp" ? { erpRole: level } : { crmRole: level });
  }

  // Say what happened at the door and in the inbox after a change that touches either.
  function report(res: any, who: string, what: string) {
    if (!res) return;
    const parts = [what];
    if (res.invite) parts.push(res.invite.sent ? `Invite emailed to ${who}.` : `The invite email didn't send (${res.invite.error}). Use "Resend invite".`);
    if (res.access) {
      if (!res.access.connected) parts.push("Cloudflare Access isn't connected, so update the \"Boost Blenz staff\" policy by hand.");
      else if (!res.access.ok) parts.push(`Cloudflare Access didn't update (${res.access.error}). Try "Sync Access now".`);
      else if (res.access.changed) parts.push("Cloudflare Access updated.");
    }
    setNote({ tone: res.invite?.sent === false || (res.access && !res.access.ok) ? "warn" : "good", text: parts.join(" ") });
  }

  async function invite(u: any) {
    const res = await api(`/api/users/${enc(u.email)}/invite`, "POST");
    if (res) setNote(res.sent ? { tone: "good", text: `Invite emailed to ${u.email}.` } : { tone: "warn", text: `The invite didn't send (${res.error}).` });
  }
  async function syncNow() {
    const res = await api("/api/users/sync-access", "POST");
    if (res) report({ access: res }, "", res.ok ? (res.changed ? "" : "Cloudflare Access already matches this list.") : "");
  }
  async function remove(u: any) {
    setRemoving(null);
    const res = await api(`/api/users/${enc(u.email)}`, "DELETE");
    report(res, u.email, `Removed ${u.name || u.email}.`);
    setOpen(null);
  }

  const myEmail = (me && me.email) || "";

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SectionTitle
        sub="Add a person, tick what they can open, and save. They get an email with how to sign in. One sign-in covers the dashboard, the ERP and the Help Desk CRM."
        right={<Btn tone="accent" size="sm" icon="users" onClick={() => setAdding((v) => !v)}>{adding ? "Cancel" : "Add person"}</Btn>}
      >
        Users &amp; Roles
      </SectionTitle>

      <AccessCard connected={accessConnected} busy={busy} onSync={syncNow} />

      {err && (
        <Card pad={12} style={{ borderLeft: "3px solid " + t.bad, display: "flex", gap: 9, alignItems: "center" }}>
          <Icon name="alert" size={15} color={t.bad} />
          <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.bad }}>{err === "403" || err === "forbidden" ? "You need the Manage-users permission to view this." : errText(err)}</span>
        </Card>
      )}
      {note && (
        <Card pad={12} style={{ borderLeft: "3px solid " + (note.tone === "good" ? t.good : t.warn), display: "flex", gap: 9, alignItems: "center" }}>
          <Icon name={note.tone === "good" ? "check" : "alert"} size={15} color={note.tone === "good" ? t.good : t.warn} />
          <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, flex: 1 }}>{note.text}</span>
          <button onClick={() => setNote(null)} style={{ background: "none", border: 0, cursor: "pointer", color: t.faint, fontSize: 16 }}>×</button>
        </Card>
      )}

      {adding && (
        <AddPerson busy={busy} onCancel={() => setAdding(false)} onSave={async (body: any) => {
          const res = await api("/api/users", "POST", body);
          if (res) { setAdding(false); report(res, body.email, `Added ${body.name || body.email}.`); }
        }} />
      )}

      {users === null ? (
        <Card pad={20}><span style={{ fontFamily: FIGTREE, color: t.muted, fontSize: 13 }}>Loading users…</span></Card>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {users.map((u) => {
            const disabled = !!u.disabled_at;
            const isOpen = open === u.email;
            const isSelf = u.email === myEmail;
            const permSet = new Set<string>(u.permissions || []);
            const screens = SCREENS.filter((p) => permSet.has(p.key)).length;
            const tools = [permSet.has("inventory") && "ERP", permSet.has("crm") && "CRM"].filter(Boolean).join(" + ");
            const levels: Levels = { erp: permSet.has("inventory") ? u.erpLevel : "", crm: permSet.has("crm") ? u.crmLevel : "" };
            return (
              <Card key={u.email} pad={0} style={{ opacity: disabled ? 0.7 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", flexWrap: "wrap" }}>
                  <Avatar name={u.name || u.email} size={34} />
                  <div style={{ flex: "1 1 200px", minWidth: 0 }}>
                    <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 14, color: t.fg, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      {u.name || u.email}
                      {isSelf && <Badge tone="neutral">You</Badge>}
                      {disabled && <Badge tone="bad">Turned off</Badge>}
                      {!isSelf && !disabled && (u.invited_at ? <Badge tone="good">Invited {String(u.invited_at).slice(0, 10)}</Badge> : <Badge tone="warn">Not invited</Badge>)}
                    </div>
                    <div style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted, marginTop: 2 }}>
                      {u.name ? u.email + " · " : ""}{screens} of {SCREENS.length} dashboard screens{tools ? " · " + tools : ""}
                    </div>
                  </div>
                  <RoleSelect value={u.role} onChange={(v: any) => patchUser(u.email, { role: v })} disabled={busy || (isSelf && u.role === "owner")} />
                  <button onClick={() => setOpen(isOpen ? null : u.email)} style={pillBtn(t)}>
                    Access <Icon name="chevron" size={13} color={t.faint} style={{ transform: isOpen ? "rotate(90deg)" : "none" }} />
                  </button>
                </div>

                {isOpen && (
                  <div style={{ padding: "4px 16px 16px", borderTop: "1px solid " + t.border }}>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 12, margin: "12px 0 14px", flexWrap: "wrap" }}>
                      <NameField value={u.name || ""} disabled={busy} onSave={(name: string) => patchUser(u.email, { name })} />
                      <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 9 }}>
                        <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>Account active</span>
                        <Toggle on={!disabled} onClick={async () => { if (!isSelf) report(await patchUser(u.email, { disabled: !disabled }), u.email, disabled ? "Turned back on." : "Turned off everywhere."); }} />
                      </div>
                    </div>
                    <AccessEditor
                      role={u.role}
                      perms={permSet}
                      levels={levels}
                      busy={busy}
                      lockAdmin={isSelf}
                      onPerms={(changes: Record<string, boolean>) => setPerms(u.email, changes)}
                      onLevel={(app: "erp" | "crm", level: string) => setLevel(u, app, level)}
                    />
                    {!isSelf && (
                      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
                        <Btn size="sm" variant="ghost" icon="mail" onClick={() => invite(u)} disabled={busy || disabled}>{u.invited_at ? "Resend invite" : "Send invite"}</Btn>
                        <Btn size="sm" variant="soft" tone="bad" icon="x" onClick={() => setRemoving(u)} disabled={busy}>Remove person</Btn>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {removing && (
        <ConfirmDialog
          title="Remove this person?"
          body={`${removing.name || removing.email} loses access to the dashboard, the ERP and the Help Desk CRM. Cloudflare Access stops sending them sign-in codes, and the ERP and CRM drop them within a few minutes. Their past activity stays in the log. You can add them again later.`}
          confirmLabel="Remove"
          onConfirm={() => remove(removing)}
          onCancel={() => setRemoving(null)}
        />
      )}

      {/* Recent audit activity */}
      <Card pad={0}>
        <div style={{ padding: "14px 16px 4px" }}><Title size={14}>Recent activity</Title></div>
        {events.length === 0 ? (
          <div style={{ padding: "14px 16px", fontFamily: FIGTREE, fontSize: 12.5, color: t.muted }}>No recorded activity yet.</div>
        ) : (
          <div>
            {events.map((e, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderTop: "1px solid " + t.border }}>
                <Icon name={auditIcon(e.action)} size={14} color={t.muted} />
                <span style={{ fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, flex: 1, minWidth: 0 }}>
                  <b>{e.actor}</b> {auditLabel(e)}
                </span>
                <span style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.faint, whiteSpace: "nowrap" }}>{e.at}</span>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ---- Add a person: everything set before the first save, then one click invites them.
function AddPerson({ busy, onSave, onCancel }: any) {
  const t = useT();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [role, setRole] = React.useState<Role>("support");
  // "Help Desk only" is a starting point, not a stored role: Support with every dashboard
  // screen off and just the Help Desk CRM on (Agent). Any switch can still be changed after.
  const [preset, setPreset] = React.useState<string>("support");
  const [perms, setPermsState] = React.useState<Set<string>>(() => new Set(ROLE_DEFAULTS.support));
  const [erp, setErp] = React.useState("staff");
  const [crm, setCrm] = React.useState("agent");
  const [invite, setInvite] = React.useState(true);

  // A role is a starting point: picking one resets the checkmarks to its defaults.
  function pickRole(v: string) {
    setPreset(v);
    if (v === HELPDESK_ONLY) {
      setRole("support");
      setPermsState(new Set(["crm"]));
      setCrm("agent");
      return;
    }
    const r = v as Role;
    setRole(r);
    setPermsState(new Set(ROLE_DEFAULTS[r]));
    setErp(r === "owner" ? "owner" : r === "ops" ? "manager" : "staff");
    setCrm(r === "owner" ? "owner" : "agent");
  }
  const levels: Levels = { erp: perms.has("inventory") ? erp : "", crm: perms.has("crm") ? crm : "" };
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <Card pad={16}>
      <Title size={14} style={{ marginBottom: 12 }}>Add a person</Title>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 180px" }}>
          <Eyebrow>Name</Eyebrow>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First Last" style={inputStyle(t)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 240px" }}>
          <Eyebrow>Email</Eyebrow>
          <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@boostblenz.com" type="email" style={inputStyle(t)} />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <Eyebrow>Starting role</Eyebrow>
          <RoleSelect value={preset} onChange={(v: string) => pickRole(v)} extra={[{ value: HELPDESK_ONLY, label: "Help Desk only" }]} />
        </label>
      </div>
      <AccessEditor
        role={role}
        perms={perms}
        levels={levels}
        busy={busy}
        onPerms={(changes: Record<string, boolean>) => setPermsState((s) => {
          const n = new Set(s);
          for (const [k, v] of Object.entries(changes)) { if (v) n.add(k); else n.delete(k); }
          return n;
        })}
        onLevel={(app: "erp" | "crm", level: string) => {
          const key = app === "erp" ? "inventory" : "crm";
          setPermsState((s) => { const n = new Set(s); if (level) n.add(key); else n.delete(key); return n; });
          if (level) (app === "erp" ? setErp : setCrm)(level);
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18, flexWrap: "wrap" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontFamily: FIGTREE, fontSize: 13, color: t.fg2 }}>
          <input type="checkbox" checked={invite} onChange={(e) => setInvite(e.target.checked)} />
          Email them the sign-in steps when I save
        </label>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10 }}>
          <Btn size="md" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Btn>
          <Btn tone="accent" size="md" disabled={busy || !valid} onClick={() => onSave({
            name: name.trim(), email: email.trim().toLowerCase(), role, permissions: [...perms],
            erpRole: role === "owner" ? null : erp, crmRole: role === "owner" ? null : crm, invite,
          })}>{invite ? "Save & send invite" : "Save"}</Btn>
        </div>
      </div>
    </Card>
  );
}

// ---- The checkmarks: dashboard screens by section (each section and the whole dashboard
// have an "all" box), then the separate tools with the level they get inside each.
function AccessEditor({ role, perms, levels, busy, lockAdmin, onPerms, onLevel }: any) {
  const t = useT();
  const allOn = SCREENS.every((p) => perms.has(p.key));
  const someOn = SCREENS.some((p) => perms.has(p.key));
  const setMany = (keys: string[], on: boolean) => {
    const changes: Record<string, boolean> = {};
    for (const k of keys) if (perms.has(k) !== on && !(lockAdmin && k === "manage_users" && !on)) changes[k] = on;
    if (Object.keys(changes).length) onPerms(changes);
  };
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <CheckRow label="Whole dashboard (every screen)" on={allOn} mixed={!allOn && someOn} disabled={busy} strong onClick={() => setMany(SCREENS.map((p) => p.key), !allOn)} />
        <span style={{ fontFamily: FIGTREE, fontSize: 12, color: t.muted }}>
          Checked = can open it. <b style={{ color: t.accent }}>Dot</b> = changed from the {ROLE_LABELS[role as Role]} default.
        </span>
      </div>
      {GROUPS.map((g) => {
        const items = SCREENS.filter((p) => p.group === g);
        const gAll = items.every((p) => perms.has(p.key));
        const gSome = items.some((p) => perms.has(p.key));
        return (
          <div key={g} style={{ marginBottom: 12 }}>
            <button disabled={busy} onClick={() => setMany(items.map((p) => p.key), !gAll)} style={{ display: "flex", alignItems: "center", gap: 7, background: "none", border: 0, padding: 0, marginBottom: 7, cursor: "pointer" }}>
              <Box on={gAll} mixed={!gAll && gSome} size={14} />
              <span style={{ fontFamily: OSWALD, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em", color: t.faint }}>{g} · all</span>
            </button>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 8 }}>
              {items.map((p) => {
                const on = perms.has(p.key);
                const overridden = on !== isRoleDefault(role, p.key); // overrides are kept minimal, so this is the override
                const lock = lockAdmin && p.key === "manage_users" && on; // can't remove own admin
                return (
                  <CheckRow key={p.key} label={p.label} on={on} dot={overridden} disabled={busy || lock}
                    title={overridden ? `Changed (role default: ${isRoleDefault(role, p.key) ? "on" : "off"})` : "Role default"}
                    onClick={() => onPerms({ [p.key]: !on })} />
                );
              })}
            </div>
          </div>
        );
      })}

      <div style={{ fontFamily: OSWALD, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em", color: t.faint, margin: "16px 0 7px" }}>Other tools · same sign-in</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 10 }}>
        <ToolLevel title="Inventory Hub (ERP)" url="erp.boostblenzhq.com" role={role} value={levels.erp} options={ERP_LEVELS} busy={busy} onChange={(v: string) => onLevel("erp", v)} />
        <ToolLevel title="Help Desk CRM" url="help.boostblenzhq.com" role={role} value={levels.crm} options={CRM_LEVELS} busy={busy} onChange={(v: string) => onLevel("crm", v)} />
      </div>
    </div>
  );
}

function ToolLevel({ title, url, role, value, options, busy, onChange }: any) {
  const t = useT();
  const sub = value === "owner" ? "Everything, including the team." : value ? options.find((o: any) => o.key === value)?.sub : "Can't open it.";
  return (
    <div style={{ padding: "10px 12px", borderRadius: t.cardRadius || 6, border: "1px solid " + (value ? "transparent" : t.border), background: value ? t.accentSoft : t.surface2 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FIGTREE, fontWeight: 700, fontSize: 12.5, color: t.fg }}>{title}</div>
          <div style={{ fontFamily: FIGTREE, fontSize: 11, color: t.faint }}>{url}</div>
        </div>
        <select value={value} disabled={busy} onChange={(e) => onChange(e.target.value)} style={selectStyle(t, busy)}>
          <option value="">No access</option>
          {role === "owner" ? <option value="owner">Owner</option> : options.map((o: any) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
      </div>
      <div style={{ fontFamily: FIGTREE, fontSize: 11.5, color: t.muted, marginTop: 6 }}>{sub}</div>
    </div>
  );
}

function CheckRow({ label, on, mixed, dot, disabled, strong, title, onClick }: any) {
  const t = useT();
  return (
    <button disabled={disabled} onClick={onClick} title={title}
      style={{
        display: "flex", alignItems: "center", gap: 9, textAlign: "left", width: strong ? "auto" : "100%",
        padding: "8px 10px", borderRadius: t.cardRadius || 6, cursor: disabled ? "not-allowed" : "pointer",
        border: "1px solid " + (on ? "transparent" : t.border),
        background: on ? t.accentSoft : t.surface2, opacity: disabled ? 0.6 : 1,
      }}>
      <Box on={on} mixed={mixed} />
      <span style={{ flex: 1, fontFamily: FIGTREE, fontWeight: strong ? 700 : 600, fontSize: 12.5, color: on ? t.fg : t.fg2 }}>{label}</span>
      {dot && <span title="Changed from the role default" style={{ width: 7, height: 7, borderRadius: 999, background: t.accent, flexShrink: 0 }} />}
    </button>
  );
}

function Box({ on, mixed, size = 18 }: any) {
  const t = useT();
  return (
    <span style={{
      width: size, height: size, borderRadius: 5, flexShrink: 0, display: "grid", placeItems: "center",
      background: on ? t.accent : "transparent", border: "1.5px solid " + (on || mixed ? t.accent : t.borderStrong),
    }}>
      {on ? <Icon name="check" size={size - 6} color="#fff" /> : mixed ? <span style={{ width: size - 9, height: 2, background: t.accent, borderRadius: 2 }} /> : null}
    </span>
  );
}

function NameField({ value, disabled, onSave }: any) {
  const t = useT();
  const [v, setV] = React.useState(value);
  React.useEffect(() => setV(value), [value]);
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5, flex: "0 1 260px" }}>
      <Eyebrow>Name</Eyebrow>
      <input value={v} disabled={disabled} onChange={(e) => setV(e.target.value)} onBlur={() => v.trim() !== value && onSave(v.trim())}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
        placeholder="Add their name" style={inputStyle(t)} />
    </label>
  );
}

// The Access door: synced automatically once the token exists, otherwise a by-hand step.
function AccessCard({ connected, busy, onSync }: any) {
  const t = useT();
  if (connected === null) return null;
  return (
    <Card pad={14} style={{ borderLeft: "3px solid " + (connected ? t.good : t.warn), display: "flex", gap: 11, alignItems: "flex-start", flexWrap: "wrap" }}>
      <Icon name="lock" size={16} color={connected ? t.good : t.warn} style={{ marginTop: 1, flexShrink: 0 }} />
      <div style={{ flex: "1 1 320px", fontFamily: FIGTREE, fontSize: 12.5, color: t.fg2, lineHeight: 1.55 }}>
        {connected ? (
          <>Cloudflare Access is <b>synced</b> with this list. Adding, turning off or removing someone here opens or closes the sign-in door for every tool automatically.</>
        ) : (
          <>Cloudflare Access <b>isn't connected yet</b>, so new people won't get a sign-in code. Until it's connected, also add their email to the <b>"Boost Blenz staff"</b> policy (Zero Trust → Access → Policies).</>
        )}
      </div>
      {connected && <Btn size="sm" variant="ghost" onClick={onSync} disabled={busy}>Sync Access now</Btn>}
    </Card>
  );
}

function errText(err: string) {
  const map: Record<string, string> = {
    invalid_email: "That email doesn't look right.",
    already_exists: "That person is already on the list. Open them below to change their access.",
    cannot_remove_self: "You can't remove yourself.",
    cannot_disable_self: "You can't turn off your own account.",
    cannot_demote_self: "You can't change your own Owner role.",
    cannot_remove_own_admin: "You can't remove your own Manage-users access.",
  };
  return map[err] || `Something went wrong (${err}).`;
}

function auditIcon(action: string) {
  if (action === "signin") return "lock";
  if (action === "helpdesk.denied" || action.endsWith("_failed")) return "alert";
  if (action.startsWith("user.permission")) return "check";
  if (action === "user.invite") return "mail";
  if (action.startsWith("access")) return "lock";
  if (action.startsWith("user")) return "users";
  return "dot";
}

function auditLabel(e: any) {
  let meta: any = {};
  try { meta = e.meta ? JSON.parse(e.meta) : {}; } catch { /* ignore */ }
  switch (e.action) {
    case "signin": return `signed in`;
    case "user.add": return `added ${e.target} as ${meta.role}`;
    case "user.remove": return `removed ${e.target}`;
    case "user.role": return `changed ${e.target} to ${meta.to}`;
    case "user.name": return `renamed ${e.target}`;
    case "user.erp_role": return `set ${e.target}'s ERP level to ${meta.to}`;
    case "user.crm_role": return `set ${e.target}'s CRM level to ${meta.to}`;
    case "user.disable": return `turned off ${e.target}`;
    case "user.enable": return `turned on ${e.target}`;
    case "user.invite": return `emailed an invite to ${e.target}`;
    case "user.invite_failed": return `tried to invite ${e.target}, but the email failed`;
    case "user.permission": return `${meta.allowed ? "granted" : "revoked"} ${meta.permission} for ${e.target}`;
    case "access.sync": return `updated Cloudflare Access (${[...(meta.added || []).map((x: string) => "+" + x), ...(meta.removed || []).map((x: string) => "−" + x)].join(", ")})`;
    case "access.sync_failed": return `couldn't update Cloudflare Access (${meta.error})`;
    case "helpdesk.denied": return `was blocked from the owner's personal mailbox (${meta.path || "Help Desk"})`;
    default: return `${e.action}${e.target ? " · " + e.target : ""}`;
  }
}

const inputStyle = (t: any) => ({ fontFamily: FIGTREE, fontSize: 13.5, padding: "10px 12px", borderRadius: t.cardRadius || 6, border: "1px solid " + t.borderStrong, background: t.surface, color: t.fg, outline: "none" });
const selectStyle = (t: any, disabled?: boolean) => ({
  fontFamily: FIGTREE, fontWeight: 700, fontSize: 12, padding: "7px 10px", borderRadius: 999, border: "1px solid " + t.borderStrong,
  background: t.surface2, color: t.fg2, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
});
const pillBtn = (t: any) => ({
  display: "inline-flex", alignItems: "center", gap: 5, background: t.surface2, border: "1px solid " + t.border, borderRadius: 999,
  padding: "7px 12px", cursor: "pointer", fontFamily: FIGTREE, fontWeight: 700, fontSize: 11.5, color: t.fg2, whiteSpace: "nowrap" as const,
});

// Small themed role dropdown.
const HELPDESK_ONLY = "helpdesk-only";

function RoleSelect({ value, onChange, disabled, extra = [] }: any) {
  const t = useT();
  return (
    <select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}
      style={{ ...selectStyle(t, disabled), letterSpacing: "0.03em", textTransform: "uppercase" }}>
      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
      {extra.map((x: { value: string; label: string }) => <option key={x.value} value={x.value}>{x.label}</option>)}
    </select>
  );
}
